import React from 'react';
import { IOrderDetails } from '../../Api/Models/Order';
import {
    Button, Dialog, DialogActions, DialogBody, DialogContent, DialogOpenChangeData, DialogOpenChangeEvent,
    DialogSurface, DialogTitle, DialogTrigger,
    Drawer, DrawerBody, DrawerFooter, DrawerHeader, DrawerHeaderTitle,
    Spinner,
    Table, TableBody, TableCell, TableRow, TableHeader, TableHeaderCell
} from '@fluentui/react-components';
import { CartRegular, Dismiss24Regular } from '@fluentui/react-icons';
import CurrencyFormat from '../Helpers/CurrencyFormat';
import { Markdown } from 'react-showdown';
import { useReorderMutation } from '../../Api/Api';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../Store/Store';
import { useLocalization } from '../Helpers/useLocalization';

const CARRIER_DISPLAY: Record<string, string> = {
    FEDEX: 'FedEx',
    UPS: 'UPS',
    USPS: 'USPS',
    DHL: 'DHL',
    AMAZON: 'Amazon Logistics',
    ONTRAC: 'OnTrac',
};

interface IOrderDetailsPanelProps {
    order?: IOrderDetails;
    onDismiss: () => void;
}

const OrderDetailsPanel: React.FC<IOrderDetailsPanelProps> = (props) => {
    const L = useLocalization();
    const [termsOpen, setTermsOpen] = React.useState(false);
    const [reorder] = useReorderMutation();
    const [reorderLoading, setReorderLoading] = React.useState(false);
    const [reorderDialogOpen, setReorderDialogOpen] = React.useState(false);
    const [reorderError, setReorderError] = React.useState<string | null>(null);
    const [unavailableProducts, setUnavailableProducts] = React.useState<string[]>([]);
    const settings = useSelector((state: RootState) => state.cart);
    const nav = useNavigate();

    const handleReorder = async () => {
        if (!props.order) return;
        setReorderLoading(true);
        setReorderError(null);
        try {
            const result = await reorder({ orderId: props.order.id }).unwrap();
            if (result.success) {
                setUnavailableProducts(result.unavailableProducts ?? []);
                setReorderDialogOpen(true);
            } else {
                setReorderError(result.error ?? L('localization_order_reorder_failed_message'));
                setReorderDialogOpen(true);
            }
        } catch {
            setReorderError(L('localization_order_reorder_failed_message'));
            setReorderDialogOpen(true);
        } finally {
            setReorderLoading(false);
        }
    };

    const navToCart = () => {
        setReorderDialogOpen(false);
        props.onDismiss();
        if (settings.selectedCartId) {
            nav(`/prepared-cart/${settings.selectedCartId}`);
        } else {
            nav('/cart');
        }
    };

    const handleOpenChange = (_:DialogOpenChangeEvent, d:DialogOpenChangeData) => {
        if (!d.open) props.onDismiss();
    }
    const isMobile = window.innerWidth < 700;
    return (
        <Drawer
            open={props.order !== undefined}
            onOpenChange={handleOpenChange}
            size={isMobile ? 'full' : 'medium'}
            >
                {props.order && <>
                    <DrawerHeader>
                        <DrawerHeaderTitle
                            action={<Button
                                onClick={props.onDismiss}
                                appearance='subtle'
                                icon={<Dismiss24Regular />}
                                aria-label='close'
                                />}
                            >
                                {L('localization_order_number_prefix')}{props.order.orderNumber}
                        </DrawerHeaderTitle>
                    </DrawerHeader>
                    <DrawerBody>
                        <div>
                            <h4>{L('localization_order_details_heading')}</h4>
                            <p>{L('localization_order_date_label')} {new Date(props.order.createDate).toLocaleDateString()}</p>
                            <p>{L('localization_order_status_detail_label')} {props.order.status}</p>
                            {props.order.presentedTerms && (
                                <>
                                    <Button appearance='subtle' onClick={() => setTermsOpen(true)}>{L('localization_order_show_terms')}</Button>
                                    <Dialog open={termsOpen} onOpenChange={(_, d) => setTermsOpen(d.open)}>
                                        <DialogSurface style={{ maxWidth: '600px', width: '90vw' }}>
                                            <DialogBody>
                                                <DialogTitle
                                                    action={
                                                        <DialogTrigger action='close'>
                                                            <Button appearance='subtle' icon={<Dismiss24Regular />} aria-label='close' />
                                                        </DialogTrigger>
                                                    }
                                                >
                                                    {L('localization_order_terms_title')}
                                                </DialogTitle>
                                                <DialogContent>
                                                    <Markdown markdown={props.order.presentedTerms} />
                                                </DialogContent>
                                            </DialogBody>
                                        </DialogSurface>
                                    </Dialog>
                                </>
                            )}
                            {props.order.shipments && props.order.shipments.length > 0 && (
                                <>
                                    <h4>{L('localization_order_shipment_tracking')}</h4>
                                    {props.order.shipments.map((s, i) => (
                                        <div key={s.shipmentTrackingId ?? i} style={{ marginBottom: '0.75em' }}>
                                            {s.shipmentCarrier && <p style={{ margin: '0 0 0.1em' }}>{L('localization_order_carrier_label')} {CARRIER_DISPLAY[s.shipmentCarrier] ?? s.shipmentCarrier}</p>}
                                            {s.shipmentTrackingNumber && <p style={{ margin: '0 0 0.1em' }}>{L('localization_order_tracking_number_label')} {s.shipmentTrackingNumber}</p>}
                                            {s.shipmentTrackingUrl && <p style={{ margin: 0 }}><a href={s.shipmentTrackingUrl} target='_blank' rel='noopener noreferrer'>{L('localization_order_track_shipment')}</a></p>}
                                        </div>
                                    ))}
                                </>
                            )}
                            <h4>{L('localization_order_products_heading')}</h4>
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHeaderCell>{L('localization_cart_col_product')}</TableHeaderCell>
                                        <TableHeaderCell style={{width: '50px'}}>{L('localization_cart_col_quantity')}</TableHeaderCell>
                                        <TableHeaderCell style={{width: '100px'}}>{L('localization_cart_col_price')}</TableHeaderCell>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {props.order.products.map((product) => (
                                        <TableRow key={product.id}>
                                            <TableCell>{product.name}</TableCell>
                                            <TableCell>{product.quantity}</TableCell>
                                            <TableCell>{product.price && <CurrencyFormat value={product.price} />}</TableCell>
                                        </TableRow>
                                    ))}
                                    {props.order.bundles.map((bundle) => (
                                        <React.Fragment key={bundle.id}>
                                            <TableRow>
                                                <TableCell>
                                                    <p style={{fontWeight:'bold'}}>{bundle.name}</p>
                                                    {bundle.products.map((product) => (
                                                        <p key={product.id}>{product.name} x {product.quantity}</p>
                                                    ))}
                                                </TableCell>
                                                <TableCell>{bundle.quantity}</TableCell>
                                                <TableCell>{bundle.price && <CurrencyFormat value={bundle.price} />}</TableCell>
                                            </TableRow>
                                        </React.Fragment>
                                    ))}
                                </TableBody>
                                <TableBody>
                                    <TableRow style={{borderBottom: 0}}>
                                        <TableCell style={{textAlign:'right'}} colSpan={2}>{L('localization_order_panel_subtotal')}</TableCell>
                                        <TableCell>{props.order.subtotal !== undefined && <CurrencyFormat value={props.order.subtotal} />}</TableCell>
                                    </TableRow>
                                    <TableRow style={{borderBottom: 0}}>
                                        <TableCell style={{textAlign:'right'}} colSpan={2}>{L('localization_order_panel_estimated_tax')}</TableCell>
                                        <TableCell>{props.order.tax !== undefined && <CurrencyFormat value={props.order.tax} />}</TableCell>
                                    </TableRow>
                                    <TableRow style={{borderBottom: 0}}>
                                        <TableCell style={{textAlign:'right'}} colSpan={2}>{L('localization_order_panel_estimated_shipping')}</TableCell>
                                        <TableCell>{props.order.shipping !== undefined && <CurrencyFormat value={props.order.shipping} />}</TableCell>
                                    </TableRow>
                                    <TableRow style={{borderBottom: 0, fontWeight: 'bold'}}>
                                        <TableCell style={{textAlign:'right'}} colSpan={2}>{L('localization_order_panel_total')}</TableCell>
                                        <TableCell>{props.order.total && <CurrencyFormat value={props.order.total} />}</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </div>
                    </DrawerBody>
                    <DrawerFooter>
                        <Button
                            icon={<CartRegular />}
                            appearance='primary'
                            onClick={handleReorder}
                            disabled={reorderLoading}
                            style={{width: '100%'}}
                        >
                            {reorderLoading ? L('localization_order_reorder_adding') : L('localization_order_reorder')}
                        </Button>
                    </DrawerFooter>
                </>}
                <Dialog open={reorderDialogOpen} onOpenChange={(_, d) => setReorderDialogOpen(d.open)}>
                    <DialogSurface>
                        <DialogBody>
                            <DialogTitle>
                                {reorderError ? L('localization_order_reorder_failed_title') : unavailableProducts.length > 0 ? L('localization_order_reorder_partial_title') : L('localization_order_reorder_success_title')}
                            </DialogTitle>
                            <DialogContent>
                                {reorderError ? (
                                    <p>{reorderError}</p>
                                ) : unavailableProducts.length > 0 ? (
                                    <div>
                                        <p>{L('localization_order_reorder_partial_message')}</p>
                                        <ul>
                                            {unavailableProducts.map((name, idx) => (
                                                <li key={idx}>{name}</li>
                                            ))}
                                        </ul>
                                    </div>
                                ) : (
                                    <p>{L('localization_order_reorder_success_message')}</p>
                                )}
                            </DialogContent>
                            <DialogActions>
                                {reorderError ? (
                                    <Button appearance='primary' onClick={() => setReorderDialogOpen(false)}>{L('localization_common_close')}</Button>
                                ) : (
                                    <>
                                        <Button onClick={() => setReorderDialogOpen(false)}>{L('localization_common_continue_shopping')}</Button>
                                        <Button appearance='primary' onClick={navToCart}>{L('localization_common_go_to_cart')}</Button>
                                    </>
                                )}
                            </DialogActions>
                        </DialogBody>
                    </DialogSurface>
                </Dialog>
        </Drawer>
    );
}

export default OrderDetailsPanel;